import { ReactNode } from "react";
import { useOnboardingData } from "./OnboardingDataContext";
import { StepCard } from "./StepCard";
import {
  ONBOARDING_STEP,
  STEP_CONFIG,
  STEP_STATUS,
  StepConfig,
  StepId,
} from "./types";
import { OnboardingState } from "./useOnboarding";

const EDITABLE_STEPS: StepId[] = [ONBOARDING_STEP.CONFIRM_ENTITY];

// Entity type is fixed once confirm-company creates the group.
const EDITABLE_UNTIL_GROUP_CREATED_STEPS: StepId[] = [
  ONBOARDING_STEP.ENTITY_TYPE,
];

type OnboardingStepperProps = {
  onboarding: OnboardingState;
  steps?: StepConfig[];
  renderStepBody: (id: StepId) => ReactNode;
};

export const OnboardingStepper = ({
  onboarding,
  steps = STEP_CONFIG,
  renderStepBody,
}: OnboardingStepperProps) => {
  const { data } = useOnboardingData();

  const isGroupCreated = Boolean(data.groupId);

  const isStepEditable = (id: StepId) => {
    if (onboarding.getStatus(id) !== STEP_STATUS.DONE) return false;
    if (EDITABLE_STEPS.includes(id)) return true;
    return EDITABLE_UNTIL_GROUP_CREATED_STEPS.includes(id) && !isGroupCreated;
  };

  return (
    <div className="t-flex t-flex-col">
      {steps.map((step, index) => {
        const isEditable = isStepEditable(step.id);
        return (
          <StepCard
            key={step.id}
            number={index + 1}
            title={step.title}
            subtitle={step.subtitle}
            badge={step.badge}
            status={onboarding.getStatus(step.id)}
            summary={onboarding.getSummary(step.id)}
            isLast={index === steps.length - 1}
            onEdit={isEditable ? () => onboarding.goToStep(step.id) : undefined}
            renderBody={() => renderStepBody(step.id)}
          />
        );
      })}
    </div>
  );
};
